import { useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError, type DeletedDocument } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";

/** Trim an ISO timestamp to "YYYY-MM-DD HH:MMZ" (UTC) for the deleted-on column. */
function formatUtc(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toISOString().replace("T", " ").replace(/:\d\d\.\d+Z$/, "Z");
}

export function DeletedDocumentsPage() {
  const api = useApi();
  useSetTopbarTabs([{ id: "restore", label: "Restore documents", active: true }]);

  const [projectIdInput, setProjectIdInput] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [docs, setDocs] = useState<DeletedDocument[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoredIds, setRestoredIds] = useState<Set<string>>(new Set());
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  async function load(e: FormEvent) {
    e.preventDefault();
    const id = projectIdInput.trim();
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    setDocs(null);
    setConfirmingId(null);
    setRowError(null);
    setRestoredIds(new Set());
    try {
      const result = await api.listDeletedDocuments(id);
      setDocs(result);
      setProjectId(id);
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 400
          ? "That doesn't look like a project GUID."
          : `${err}`,
      );
    } finally {
      setLoading(false);
    }
  }

  async function restore(doc: DeletedDocument) {
    if (!projectId) return;
    setRestoringId(doc.documentId);
    setRowError(null);
    try {
      await api.restoreDocument(projectId, doc.documentId);
      setRestoredIds((prev) => new Set(prev).add(doc.documentId));
      setConfirmingId(null);
    } catch (err) {
      setRowError({ id: doc.documentId, message: `${err}` });
    } finally {
      setRestoringId(null);
    }
  }

  const remaining = docs?.filter((d) => !restoredIds.has(d.documentId)).length ?? 0;

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Restore documents</div>
        <h1>Restore deleted documents</h1>
        <p>
          List the soft-deleted documents and drawings in a project and bring them back. A restored item
          re-appears in the project immediately, with its revisions and annotations intact. If the customer
          re-uploaded after deleting, restoring can leave two items sharing a sheet number — check the live
          project first.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Project
          <span className="hint">project id (GUID)</span>
        </header>
        <div className="body">
          <form className="inline-form" onSubmit={load}>
            <input
              type="text"
              placeholder="00000000-0000-0000-0000-000000000000"
              spellCheck={false}
              autoComplete="off"
              value={projectIdInput}
              onChange={(e) => setProjectIdInput(e.target.value)}
            />
            <button
              className={"btn btn-primary" + (loading ? " loading" : "")}
              disabled={loading || !projectIdInput.trim()}
            >
              <span className="glyph" /> Find deleted
            </button>
          </form>
          {loadError && (
            <div className="callout error" style={{ marginTop: 16 }}>
              <span className="mk">err</span>
              <span>{loadError}</span>
            </div>
          )}
        </div>
      </section>

      {docs && (
        <section className="panel reveal" data-d="3">
          <header>
            <span className="idx">02</span> Soft-deleted items
            <span className="hint">
              {remaining} recoverable{restoredIds.size > 0 ? ` · ${restoredIds.size} restored` : ""}
            </span>
          </header>
          <div className="body">
            {docs.length === 0 ? (
              <div className="callout ok">
                <span className="mk">ok</span>
                <span>No soft-deleted documents in this project.</span>
              </div>
            ) : (
              <>
                <div style={{ border: "1px solid var(--line)" }}>
                  <table className="grid">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Sheet ID</th>
                        <th style={{ textAlign: "right" }}>Rev</th>
                        <th>Deleted (UTC)</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((d) => {
                        const restored = restoredIds.has(d.documentId);
                        return (
                          <tr key={d.documentId} className={restored ? "faint" : undefined}>
                            <td>{d.name}</td>
                            <td>
                              <span className={"tag" + (d.type === "Drawing" ? " cyan" : "")}>{d.type}</span>
                            </td>
                            <td className="mono">{d.sheetId ?? <span className="faint">—</span>}</td>
                            <td style={{ textAlign: "right" }}>{d.latestRevisionNumber}</td>
                            <td className="mono">{formatUtc(d.deletedOnUtc)}</td>
                            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              {restored ? (
                                <span className="tag" style={{ color: "var(--lime)" }}>
                                  restored ✓
                                </span>
                              ) : confirmingId === d.documentId ? (
                                <span className="btn-row" style={{ justifyContent: "flex-end" }}>
                                  <button
                                    className={"btn btn-primary btn-sm" + (restoringId === d.documentId ? " loading" : "")}
                                    disabled={restoringId === d.documentId}
                                    onClick={() => restore(d)}
                                  >
                                    <span className="glyph" /> Confirm
                                  </button>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    disabled={restoringId === d.documentId}
                                    onClick={() => setConfirmingId(null)}
                                  >
                                    Cancel
                                  </button>
                                </span>
                              ) : (
                                <button
                                  className="btn btn-sm"
                                  onClick={() => {
                                    setConfirmingId(d.documentId);
                                    setRowError(null);
                                  }}
                                >
                                  <span className="glyph" /> Restore
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {rowError && (
                  <div className="callout error" style={{ marginTop: 16 }}>
                    <span className="mk">err</span>
                    <span>{rowError.message}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}
